import { Dialog, Transition } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useVoucherOrderAddition } from "app/hooks/shared/useVoucherOrderAddition";
import { useVoucherOrdersAddition } from "app/hooks/shared/useVoucherOrdersAddition";
import { useOrderContext } from "app/providers/OrderProvider";
import { motion } from "framer-motion";
import { Fragment, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { voucherState } from "shared/store/voucher/voucherStore";
import Icon, { IconTypes } from "shared/ui/icon";
import { useSnapshot } from "valtio";
import { z } from "zod";
import TextInput from "../../checkout/send-to-email/components/Textinput";
import { TextInputFormFields } from "../TextInputFormFields";
import { VoucherButton } from "./components/VoucherButton";
import { VoucherHeader } from "./components/VoucherHeader";
import { voucherBtnVariants } from "./VoucherModal";

const validationSchema = z.object({
  Voucher: z.string().min(1, { message: "Voucher is verplicht" }),
});

type VoucherModalOrderProps = {
  orderID: string | null;
  orderType: keyof typeof orderTypeMapping;
  alreadyPaid?: boolean;
};

const orderTypeMapping = {
  d: "order",
  p: "standardOrder",
};
export default function VoucherModalOrder({
  orderID,
  orderType,
  alreadyPaid = false,
}: VoucherModalOrderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TextInputFormFields>({
    resolver: zodResolver(validationSchema),
  });

  const { voucher } = useSnapshot(voucherState);
  const { hasVouchersAlready } = useOrderContext();
  const { addVoucherToOrders, isLoading, status } = useVoucherOrdersAddition();

  const { addVoucherToOrder, status: panelStatus } = useVoucherOrderAddition({
    orderID,
    orderType,
  });

  // Function to handle form submission
  const onSubmit: SubmitHandler<TextInputFormFields> = async (data) => {
    if (orderType === "p") {
      const success = await addVoucherToOrder(data.Voucher);
      if (success) {
        onModalClose();
      }
    } else {
      const success = await addVoucherToOrders(
        data.Voucher,
        orderTypeMapping[orderType],
      );
      if (success) {
        onModalClose();
      }
    }
  };

  // Function to close the modal and reset form values
  const onModalClose = () => {
    setIsOpen(false); // Close the modal by setting isOpen to false
    reset(); // Reset the form values
  };

  //Conditiions to disable voucher selection
  const disableVoucherSelection =
    isLoading ||
    !!voucher ||
    hasVouchersAlready ||
    panelStatus === "loading" ||
    alreadyPaid;

  const statusCheck = status ? status : panelStatus;

  return (
    <>
      <motion.button
        initial={false}
        variants={voucherBtnVariants}
        animate={!disableVoucherSelection ? "shown" : "hidden"}
        style={{ pointerEvents: !disableVoucherSelection ? "auto" : "none" }}
        aria-hidden={disableVoucherSelection}
        tabIndex={!disableVoucherSelection ? 0 : -1}
        onClick={() => setIsOpen(true)}
      >
        <h5 className="gg-flex gg-whitespace-nowrap gg-text-yellow gg-underline hover:gg-text-yellow-400 hover:gg-underline">
          <Icon name={IconTypes.DISCOUNT} className="gg-mr-2.5" />
          Voeg voucher toe
        </h5>
      </motion.button>

      <Transition as={Dialog} show={isOpen} onClose={onModalClose}>
        <Transition.Child
          as={Fragment}
          enter="gg-ease-out gg-duration-300"
          enterFrom="gg-opacity-0 gg-scale-100"
          enterTo="gg-opacity-100 gg-scale-100"
          leave="gg-ease-in gg-duration-100"
          leaveFrom="gg-opacity-100 gg-scale-100"
          leaveTo="gg-opacity-0 gg-scale-95"
        >
          <div className="gg-fixed gg-left-0 gg-top-0 gg-z-50 gg-grid gg-h-full gg-w-screen gg-place-items-center gg-overflow-x-hidden gg-backdrop-blur-md lg:gg-inset-0 lg:gg-h-auto">
            <Dialog.Panel className="gg-grid gg-h-fit gg-w-full gg-grid-cols-9 gg-rounded gg-bg-white gg-shadow-lg sm:gg-max-w-md lg:gg-max-w-lg">
              {/* Header */}
              <VoucherHeader onModalClose={onModalClose} />
              {/* Inputs */}
              <form
                id="sendVoucherOrderForm"
                onSubmit={handleSubmit(onSubmit)}
                className="gg-col-span-9 gg-w-full gg-px-5 gg-pb-8 gg-pt-4 sm:gg-px-6 lg:gg-px-8 lg:gg-pb-12"
              >
                <TextInput
                  register={register}
                  id="voucher"
                  label="Voucher"
                  ariaLabel="Voucher"
                  errors={errors}
                  showLabel={false}
                  className="lg:gg-max-w-full"
                />
              </form>
              {/* Inputs end */}
              {/* Button */}
              <div className="gg-col-span-9 gg-flex gg-w-full gg-justify-end gg-border-t gg-border-gray-200 gg-px-6 gg-pb-6 gg-pt-5 lg:gg-px-8 lg:gg-pb-8">
                <VoucherButton
                  formName="sendVoucherOrderForm"
                  status={statusCheck}
                />
              </div>
              {/* Button */}
            </Dialog.Panel>
          </div>
        </Transition.Child>
      </Transition>
    </>
  );
}
